/** Explicit email delivery of a just-created workspace invitation through the local
 * workspace-invite function. The acceptance token goes only to that function, which
 * checks its hash and sends one message per delivery ID. Repeating a delivery ID (after
 * a lost answer) reports the recorded attempt and never sends a second copy.
 * "unknown" means the mail server may have received it: never resend automatically. */
import {ApiError} from './errors.ts';

export type InvitationEmailState='accepted'|'failed'|'unknown'|'sending';
export interface InvitationEmailRequest {workspaceId:string;invitationId:string;deliveryId:string;token:string}
export type InvitationEmailSender=(request:InvitationEmailRequest,signal?:AbortSignal)=>Promise<InvitationEmailState>;
export interface InvitationEmailConfig {functionsUrl:string;anonKey:string;getAccessToken:()=>Promise<string|null>;fetch?:typeof fetch;timeoutMs?:number}

const UUID=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const STATES:readonly InvitationEmailState[]=['accepted','failed','unknown','sending'];

/** A network failure or timeout throws NetworkError: the outcome is not known, so the
 * caller asks again with the same delivery ID rather than starting a new attempt. */
export function createInvitationEmailSender(config:InvitationEmailConfig):InvitationEmailSender{
 const doFetch=config.fetch??globalThis.fetch.bind(globalThis);
 return async({workspaceId,invitationId,deliveryId,token},signal)=>{
  if(![workspaceId,invitationId,deliveryId].every(id=>UUID.test(id))||!/^[a-f0-9]{64}$/.test(token))throw Error('invalid_invitation_email');
  const access=await config.getAccessToken();
  if(!access)throw new ApiError(401);
  const timeout=AbortSignal.timeout(config.timeoutMs??60000);
  let response:Response;
  try{
   response=await doFetch(`${config.functionsUrl}/workspace-invite`,{method:'POST',signal:signal?AbortSignal.any([signal,timeout]):timeout,
    headers:{apikey:config.anonKey,Authorization:`Bearer ${access}`,'Content-Type':'application/json'},
    body:JSON.stringify({workspace_id:workspaceId,invitation_id:invitationId,delivery_id:deliveryId,token})});
  }catch(error){if(signal?.aborted)throw error;throw Object.assign(new Error('network_failure'),{name:'NetworkError'});}
  let body:{state?:unknown;code?:unknown}={};
  try{body=await response.json();}catch{/* gateway error without JSON */}
  if(response.ok&&STATES.includes(body.state as InvitationEmailState))return body.state as InvitationEmailState;
  // Gateway/server failures leave the attempt open; only "email not configured" is final.
  if(response.ok||(response.status>=500&&body.code!=='email_not_configured'))throw Object.assign(new Error('network_failure'),{name:'NetworkError'});
  throw new ApiError(response.status,undefined,typeof body.code==='string'&&/^[a-z_]{3,40}$/.test(body.code)?body.code:undefined);
 };
}
